import { Injectable, inject } from '@angular/core';
import { Subject, Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { SocketService } from '../../../core/services/socket.service';
import { PreloadPassword } from 'src/app/shared/models/preloadPassword.model';
import { PreloadUrl } from 'src/app/shared/models/preloadUrl.model';

/**
 * PasswordService
 * @class PasswordService
 */
@Injectable({ providedIn: 'root' })
export class PasswordService {

  /**
   * Injected HttpClient
   * @type {HttpClient}
   */
  private http = inject(HttpClient);

  /**
   * Injected SocketService
   * @type {SocketService}
   */
  private socketService = inject(SocketService);

  /**
   * base api url
   * @type {string}
   */
  private baseApiUrl = environment.apiUrl + 'company';

  /**
   * echo instance
   * @type {any}
   */
  private echoInstance = null;

  /**
   * Subject for emitting preloadPassword socket events
   * @type {Subject<IpreloadWithType>}
   */
  private socketPreloadPassword$: Subject<IpreloadWithType> = new Subject();

  /**
   * Subject for emitting preloadURL socket events
   * @type {Subject<IpreloadWithType>}
   */
  private socketPreloadUrl$: Subject<IpreloadWithType> = new Subject();

  /**
   * Status of preloadURL
   * @type {number}
   */
  private statusPreloadUrl = 0;

  /**
   * Status of preloadPassword
   * @type {number}
   */
  private statusPreloadPassword = 0;


  /**
   * Starts the socket connection for listening to preloadURL events
   * @returns {void}
   */
  startPreloadUrlSocket() {
    this.statusPreloadUrl += 1;
    if (this.statusPreloadUrl === 1) {
      this.echoInstance = this.socketService.getEchoInstance();
      this.echoInstance.private(`company.${this.socketService.companyId}.preload-url`)
        .listen('.CompanyPreloadUrlAddedEvent', (event) => {
          this.socketPreloadUrl$.next({ action: 'add', password: event.preloadUrl });
        })
        .listen('.CompanyPreloadUrlUpdatedEvent', (event) => {
          this.socketPreloadUrl$.next({ action: 'update', password: event.preloadUrls });
        })
        .listen('.CompanyPreloadUrlUpdatedForGroupEvent', (event) => {
          this.socketPreloadUrl$.next({ action: 'updateGroup', passwords: event.preloadUrls });
        })
        .listen('.CompanyPreloadUrlDeletedEvent', (event) => {
          this.socketPreloadUrl$.next({ action: 'delete', passwords: event.preloadUrls });
        })
        .listen('.CompanyPreloadUrlDeletedAllEvent', (event) => {
          this.socketPreloadUrl$.next({ action: 'deleteALL' });
        })
        .listen('.CompanyPreloadUrlAddedForGroupEvent', (event) => {
          this.socketPreloadUrl$.next({ action: 'addGroup', passwords: event.preloadUrls });
        })
        .listen('.CompanyPreloadUrlDeletedForGroupEvent', (event) => {
          this.socketPreloadUrl$.next({ action: 'deleteGroup', passwords: event.preloadUrls });
        });
    }
  }

  /**
   * Starts the socket connection for listening to preloadPassword events
   * @returns {void}
   */
  startPreloadPasswordSocket() {
    this.statusPreloadPassword += 1;
    if (this.statusPreloadPassword === 1) {
      this.echoInstance = this.socketService.getEchoInstance();
      this.echoInstance.private(`company.${this.socketService.companyId}.preload-password`)
        .listen('.CompanyPreloadPasswordAddedEvent', (event) => {
          this.socketPreloadPassword$.next({ action: 'add', password: event.preloadPassword });
        })
        .listen('.CompanyPreloadPasswordUpdatedEvent', (event) => {
          this.socketPreloadPassword$.next({ action: 'update', password: event.preloadPasswords });
        })
        .listen('.CompanyPreloadPasswordUpdatedForGroupEvent', (event) => {
          this.socketPreloadPassword$.next({ action: 'updateGroup', passwords: event.preloadPasswords });
        })
        .listen('.CompanyPreloadPasswordDeletedEvent', (event) => {
          this.socketPreloadPassword$.next({ action: 'delete', passwords: event.preloadPasswords });
        })
        .listen('.CompanyPreloadPasswordDeletedAllEvent', (event) => {
          this.socketPreloadPassword$.next({ action: 'deleteALL' });
        })
        .listen('.CompanyPreloadPasswordAddedForGroupEvent', (event) => {
          this.socketPreloadPassword$.next({ action: 'addGroup', passwords: event.preloadPasswords });
        })
        .listen('.CompanyPreloadPasswordDeletedForGroupEvent', (event) => {
          this.socketPreloadPassword$.next({ action: 'deleteGroup', passwords: event.preloadPasswords });
        });
    }
  }

  /**
   * Subscribes to socket events related to preloadURLs
   *
   * @returns {Observable<IpreloadWithType>}
   */
  subSocketPreloadUrl(): Observable<IpreloadWithType> {
    return this.socketPreloadUrl$;
  }

  /**
   * Subscribes to socket events related to preloadPasswords
   *
   * @returns {Observable<IpreloadWithType>}
   */
  subSocketPreloadPassword(): Observable<IpreloadWithType> {
    return this.socketPreloadPassword$;
  }

  /**
   * Stops the socket connection for preloadURL events
   * Decreases the status counter and leaves the socket channel if there are no more active connections
   * @returns {void}
   */
  stopPreloadUrlSocket() {
    this.statusPreloadUrl -= 1;
    if (this.echoInstance && this.statusPreloadUrl === 0) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.preload-url`);
    }
  }

  /**
   * Stops the socket connection for preloadPassword events
   * Decreases the status counter and leaves the socket channel if there are no more active connections
   * @returns {void}
   */
  stopPreloadPasswordSocket() {
    this.statusPreloadPassword -= 1;
    if (this.echoInstance && this.statusPreloadPassword === 0) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.preload-password`);
    }
  }

  /**
   * Retrieves preloadPasswords based on the provided filters
   * @param {any} filters
   * @returns {Promise<any>}
   */
  getPreloadPassword(filters): Promise<any> {
    return firstValueFrom(this.http.get(`${this.baseApiUrl}/preloadPassword`, { params: filters }));
  }

  /**
   * Adds a preloadPassword
   * @param {any} data
   * @returns {Promise<any>}
   */
  addPreloadPassword(data): Promise<any> {
    return firstValueFrom(this.http.post(`${this.baseApiUrl}/preloadPassword`, data));
  }

  /**
   * Adds multiple preloadPasswords
   *
   * @param {any} data
   * @returns {Promise<any>}
   */
  addPreloadPasswords(data): Promise<any> {
    return firstValueFrom(this.http.post(`${this.baseApiUrl}/preloadPassword/import`, data));
  }

  /**
   * Updates an existing preloadPassword
   *
   * @param {any} data
   * @returns {Promise<any>}
   */
  updatePreloadPassword(data): Promise<any> {
    return firstValueFrom(this.http.put(`${this.baseApiUrl}/preloadPassword`, data));
  }

  /**
   * Retrieves a credential ID for a preloadPassword
   * @param {any} data
   * @returns {Promise<any>}
   */
  getCredentialId(data): Promise<any> {
    return firstValueFrom(this.http.post(`${this.baseApiUrl}/preloadPassword/credential-id`, data));
  }

  /**
   * Deletes an existing preloadPassword
   *
   * @param {any} data
   * @returns {Promise<any>}
   */
  deletePreloadPassword(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', `${this.baseApiUrl}/preloadPassword`, { body: data }));
  }

  /**
   * Deletes multiple preloadPasswords
   *
   * @param {any} data
   * @returns {Promise<any>}
   */
  deletePreloadPasswords(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', `${this.baseApiUrl}/preloadPasswords`, { body: data }));
  }

  /**
   * Deletes multiple preloadPasswords
   * @param {any} data
   * @returns {Promise<any>}
   */
  deleteAllPreloadPassword(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', `${this.baseApiUrl}/preloadPassword/all`, { body: data }));
  }

  /**
   * Updates images for preloadPasswords
   * @param {any} data
   * @returns {Promise<any>}
   */
  updateImagePreloadPasswords(data): Promise<any> {
    return firstValueFrom(this.http.put(`${this.baseApiUrl}/preloadPassword/images`, data));
  }

  /**
   * Retrieves preloadURLs based on the provided filters
   * @param {any} filters
   * @returns {Promise<any>}
   */
  getPreloadUrl(filters): Promise<any> {
    return firstValueFrom(this.http.get(`${this.baseApiUrl}/preloadUrl`, { params: filters }));
  }

  /**
   * Adds a preloadURL.
   * @param {any} data
   * @returns {Promise<any>}
   */
  addPreloadUrl(data): Promise<any> {
    return firstValueFrom(this.http.post(`${this.baseApiUrl}/preloadUrl`, data));
  }

  /**
   * Adds multiple preloadURLs
   * @param {any} data
   * @returns {Promise<any>}
   */
  addPreloadUrls(data): Promise<any> {
    return firstValueFrom(this.http.post(`${this.baseApiUrl}/preloadUrl/import`, data));
  }

  /**
   * Updates an existing preloadURL
   * @param {any} data
   * @returns {Promise<any>}
   */
  updatePreloadUrl(data): Promise<any> {
    return firstValueFrom(this.http.put(`${this.baseApiUrl}/preloadUrl`, data));
  }

  /**
   * Deletes an existing preloadURL
   * @param {any} data
   * @returns {Promise<any>}
   */
  deletePreloadUrl(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', `${this.baseApiUrl}/preloadUrl`, { body: data }));
  }

  /**
   * Deletes multiple preloadURLs
   * @param {any} data
   * @returns {Promise<any>}
   */
  deletePreloadUrls(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', `${this.baseApiUrl}/preloadUrls`, { body: data }));
  }

  /**
 * Deletes all preloadURLs
 * @param {any} data
 * @returns {Promise<any>}
 */
  deleteAllPreloadUrl(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', `${this.baseApiUrl}/preloadUrl/all`, { body: data }));
  }

  /**
 * Updates images for preloadURLs
 * @param {any} data
 * @returns {Promise<any>}
 */
  updateImagePreloadUrls(data): Promise<any> {
    return firstValueFrom(this.http.put(`${this.baseApiUrl}/preloadUrl/images`, data));
  }

  /**
   * Logs the visibility of a preloadPassword
   * @param {any} data
   * @returns {Promise<any>}
   */
  pingLogPasswordVisible(data): Promise<any> {
    return firstValueFrom(this.http.post(`${this.baseApiUrl}/preloadPassword/visible`, data));
  }

  /**
 * Updates preloadPassword image
 * @param {any} data
 * @returns {Promise<any>}
 */
  updateImagePreloadPassword(data): Promise<any> {
    return firstValueFrom(this.http.put(`${this.baseApiUrl}/preloadPassword/image`, data));
  }

  /**
 * Updates preloadURL image
 * @param {any} data
 * @returns {Promise<any>}
 */
  updateImagePreloadUrl(data): Promise<any> {
    return firstValueFrom(this.http.put(`${this.baseApiUrl}/preloadUrl/image`, data));
  }

  /**
 * export preloadPassword with password
 * @param {any} data
 * @returns {Promise<any>}
 */
  exportPreloadPasswordWithPassword(data): Promise<any> {
    return firstValueFrom(this.http.post(`${this.baseApiUrl}/preloadPassword/export`, data));
  }
  /**
 * export all preloadPasswords
 * @param {any} data
 * @returns {Promise<any>}
 */
  exportAllPreloadPassword(data): Promise<any> {
    return firstValueFrom(this.http.post(`${this.baseApiUrl}/preloadPassword/export/all`, data));
  }
  /**
 * export all preloadUrls
 * @param {any} data
 * @returns {Promise<any>}
 */
  exportAllPreloadUrl(data): Promise<any> {
    return firstValueFrom(this.http.post(`${this.baseApiUrl}/preloadUrl/export/all`, data));
  }
  /**
 * delete preloadPassword with excel
 * @param {any} data
 * @returns {Promise<any>}
 */
  deletePasswordWithExcel(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', `${this.baseApiUrl}/preloadPassword/template`, { body: data }));
  }
}

/**
 * Interface representing the preload action types for preloadPasswords/preloadURLs
 * @description This is used to manage various actions: add, update, delete, updateGroup, deleteALL, addGroup, deleteGroup
 * @interface
 */
export interface IpreloadWithType {
  /**
   * Array of preload passwords or URLs
   * @type {PreloadPassword[] | PreloadUrl[]}
   */
  password?: PreloadPassword | PreloadUrl;
  passwords?: PreloadPassword[] | PreloadUrl[] | number[];
  /**
 * Action type
 * @type {'add' | 'update' | 'delete' | 'updateGroup' | 'deleteALL' | 'addGroup' | 'deleteGroup'}
 */
  action: 'add' | 'update' | 'delete' | 'updateGroup' | 'deleteALL' | 'addGroup' | 'deleteGroup' | 'addGroup' | 'deleteGroup';

}
